import { skipToken } from "@reduxjs/toolkit/query";
import { SvgIcon } from "@progress/kendo-react-common";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { dataIcon } from "@progress/kendo-svg-icons";
import { useGetDataConfidenceQuery } from "../../../api/dataConfidenceApi";
import { useDataConfidenceRequest } from "../../../hooks/useDataConfidenceRequest";
import { SovraSkeleton } from "../../../components/skeleton/sovraSkeleton";

const clampScore = (score: number): number => Math.max(0, Math.min(100, Math.round(score)));

const DataConfidence: React.FC = () => {
    const request = useDataConfidenceRequest();
    const { data, isError, isFetching, isLoading } = useGetDataConfidenceQuery(request);
    const isUnavailable = request === skipToken;
    const confidenceScore = data ? clampScore(data.overall_score) : 0;

    return (
        <Card className="border border-light-subtle rounded-3 shadow-sm overflow-hidden">
            <CardBody className="p-0">
                <div className="px-3 px-lg-4 pt-3 pb-4">
                    <div className="d-flex align-items-start gap-2 mb-3">
                        <span
                            className="bg-success rounded-pill flex-shrink-0 mt-1"
                            style={{ width: "4px", minHeight: "24px" }}
                            aria-hidden="true"
                        />
                        <div>
                            <h2 className="h5 fw-bold text-body mb-1">Data Confidence</h2>
                            <p className="small text-muted mb-0">Quality and completeness of input data</p>
                        </div>
                    </div>

                    <div
                        className="border rounded-3 p-3 p-lg-4"
                        style={{
                            backgroundColor: "#eafcf5",
                            borderColor: "#80ead1"
                        }}
                    >
                        {(isLoading || isFetching) && (
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-4">
                                <div className="flex-grow-1">
                                    <SovraSkeleton shape="text" style={{ width: 180, height: 20 }} />
                                    <SovraSkeleton shape="text" style={{ width: 96, height: 44 }} className="my-2" />
                                    <SovraSkeleton shape="text" style={{ width: "100%", height: 18 }} />
                                    <SovraSkeleton shape="text" style={{ width: "82%", height: 18 }} className="mt-2" />
                                </div>
                                <SovraSkeleton shape="circle" style={{ width: 64, height: 64 }} />
                            </div>
                        )}

                        {!isLoading && !isFetching && isUnavailable && (
                            <p className="mb-0 text-muted">Data confidence is unavailable until a commercial plan is selected.</p>
                        )}

                        {!isLoading && !isFetching && !isUnavailable && isError && (
                            <p className="mb-0 text-danger">Unable to load data confidence.</p>
                        )}

                        {!isLoading && !isFetching && !isUnavailable && !isError && data && (
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-4">
                                <div className="flex-grow-1">
                                    <p className="text-body-secondary mb-2">Overall Confidence Score</p>
                                    <div className="display-6 fw-semibold text-success mb-2">
                                        {confidenceScore}
                                        %
                                    </div>
                                    <p className="mb-2">
                                        Confidence score reflects data completeness, historical coverage, and alignment across price,
                                        promotion and retail media datasets.
                                    </p>
                                    <p className="mb-0">
                                        Recommendation strength adjusts automatically as new sales, promotion and media data is ingested.
                                    </p>
                                </div>

                                <div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                    aria-label={`${confidenceScore}% confidence score`}
                                    role="img"
                                    style={{
                                        width: 64,
                                        height: 64,
                                        background: `conic-gradient(#008777 0 ${confidenceScore}%, #dfe7e6 ${confidenceScore}% 100%)`
                                    }}
                                >
                                    <span
                                        className="rounded-circle bg-white d-inline-flex align-items-center justify-content-center text-success"
                                        style={{ width: 48, height: 48 }}
                                    >
                                        <SvgIcon icon={dataIcon} size="large" />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default DataConfidence;
